import dayjs from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { showToast } from "../../../../Components/ToastNotification";
import useSocket from "../../../../services/socketService";
import { Agency } from "../../../../type/Agency";
import { API_ENDPOINTS } from "../../../../utils/api";
import createAxiosInstance from "../../../../utils/axiosInstance";
import MyRequestContainer from "./MyRequestContainer";

type BedNeededProps = {
  myShelter: Agency | null;
  agencies: any;
  agenciesById: any;
  cachedResponseId: string | null;
  updateIsMyRequestLoading: () => void;
  updateCachedResponseId: (id: string | null) => void;
};

interface Response {
  responseId: string;
  response: string;
}

interface Request {
  requestId: string;
  responses: Response[];
  status: string;
  createdAt: string;
}

const MyRequest: React.FC<BedNeededProps> = ({ myShelter, agencies, agenciesById, cachedResponseId, updateIsMyRequestLoading, updateCachedResponseId }) => {
  const axiosInstance = createAxiosInstance();

  const [request, setRequest] = useState([]);
  const requestRef = useRef<Request[]>([]);
  const [cachedCloseRequestId, setCachedCloseRequestId] = useState<string | null>(null);
  const [myRequestIds, setMyReuqestIds] = useState<string[]>([]);
  const myRequestIdsRef = useRef<string[]>([]);
  const cachedResponseIdRef = useRef(cachedResponseId);
  const cachedCloseRequestIdRef = useRef(cachedCloseRequestId);

  const { on } = useSocket("requests");

  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  useEffect(() => {
    const unsubscribe = on("message", (data) => {
      if ((data.eventType === "updated" || data.eventType === "canceled" || data.eventType === "success" || data.eventType === "reject") && cachedResponseIdRef.current !== data.data.responseId && cachedCloseRequestIdRef.current !== data.data.requestId && myRequestIdsRef.current.includes(data.data.requestId)) {
        if (data.eventType === "updated" || data.eventType === "canceled") {
          showToast("An agency has changed the status of a request.", "info");
        } else if (data.eventType === "success" || data.eventType === "reject") {
          const isFirstResponse = requestRef.current?.some((req) =>
            req.requestId === data.data.requestId &&
            req.responses.some(
              (response: Response) =>
                response.responseId === data.data.responseId &&
                response.response === "null"
            )
          );
    
          if (isFirstResponse) {
            showToast("An agency responded to your request.", "info");
          } else {
            showToast("An agency changed a response.", "info");
          }
        }
        getMyRequest();
      }
      updateCachedResponseId(null);
      updateCachedCloseRequestId(null);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    cachedResponseIdRef.current = cachedResponseId;
  }, [cachedResponseId]);

  useEffect(() => {
    cachedCloseRequestIdRef.current = cachedCloseRequestId;
  }, [cachedCloseRequestId]);

  useEffect(() => {
    myRequestIdsRef.current = myRequestIds;
  }, [myRequestIds]);

  useEffect(() => {
    getMyRequest();
  }, []);

  const updateCachedCloseRequestId = (id: string | null) => {
    setCachedCloseRequestId(id);
  }

  const getMyRequest = async () => {
    try {
      const response = await axiosInstance.get(`${API_ENDPOINTS.BEDFINDER.GETMYREQUEST}?status=open&expand=need,responses`);

      const data = response.data; // Extract the actual data

      updateIsMyRequestLoading();
      const ids = data.map((it: any) => it.requestId);
      setMyReuqestIds(ids);
      setRequest(data);
    } catch (error) {
      console.log(error);
    }
  };

  const requests = useMemo(
    () =>
      request
        .filter((it: any) => it.status !== "closed")
        .map((it: any) => ({ ...it, createdAt: dayjs(it.createdAt) }))
        .sort((a, b) => b.createdAt.diff(a.createdAt))
        .map(it => ({ ...it, createdAt: it.createdAt.toISOString() })),
    [request]
  );

  return (
    <div className="cont">
      {requests
        ?.filter(it => it.status)
        .map(req => (
          <MyRequestContainer request={req} myShelter={myShelter} key={req.requestId} agencies={agencies} agenciesById={agenciesById} updateCachedCloseRequestId={updateCachedCloseRequestId} getMyRequest={getMyRequest} />
        )
        )}
      {requests.length === 0 && (
        <div className="card mt-3 rounded-lg">You have not requested any beds. </div>
      )}
    </div>
  );
};

export default MyRequest;
